// GST / HTSC helpers backing the Lead duplicate-detection flow: checking a
// typed GSTIN or HTSC number against every other lead, and computing the
// "update within 2 days" deadline for leads saved without them.
import { Lead } from '@/types'

// Standard 15-character GSTIN: 2-digit state code, 10-char PAN (5 letters,
// 4 digits, 1 letter), 1 entity code (digit or letter), literal 'Z', 1
// checksum char.
const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/

export function normalizeGst(gst: string): string {
  return String(gst || '').trim().toUpperCase()
}

export function isValidGstFormat(gst: string): boolean {
  return GSTIN_PATTERN.test(normalizeGst(gst))
}

/** HTSC numbers are entered inconsistently (spaces, dashes, mixed case) — compare on the bare value. */
export function normalizeHtsc(htsc: string): string {
  return String(htsc || '').trim().toUpperCase().replace(/[\s-]/g, '')
}

/** Find another lead already registered under this exact GST number. */
export function findLeadByGst(gst: string, leads: Lead[], excludeId?: string): Lead | undefined {
  const target = normalizeGst(gst)
  if (!target) return undefined
  return leads.find((l) => l.id !== excludeId && normalizeGst(l.gstNumber || '') === target)
}

/** Find another lead already registered under this exact HTSC number. */
export function findLeadByHtsc(htsc: string, leads: Lead[], excludeId?: string): Lead | undefined {
  const target = normalizeHtsc(htsc)
  if (!target) return undefined
  return leads.find((l) => l.id !== excludeId && normalizeHtsc(l.htscNumber || '') === target)
}

/** Add N calendar days to a date — used for the HTSC/GST update deadline,
 *  which runs 2 full days from lead creation (see computeLeadDeactiveState). */
export function addDays(from: Date, days: number): Date {
  const result = new Date(from)
  result.setDate(result.getDate() + days)
  return result
}

export function formatDate(d: Date): string {
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

/**
 * Single source of truth for a lead's Deactive status: a lead missing its
 * HTSC Number and/or GST Number stays Active for a 2-day grace period counted
 * from the lead's own creation date — not from whenever the record happens to
 * be saved/edited — then flips to Deactive once that due date has passed.
 * Once both fields are present the lead is Active again immediately.
 *
 * Deactive is a *flag*, not a gate: it drives the red mark and the reminder
 * notifications, and never blocks a stage change or a proposal.
 *
 * The due date is always recomputed from createdAt rather than trusted from
 * a stored value, so it can't drift out of sync with a separately-set
 * gstVerifyDueDate.
 */
export function computeLeadDeactiveState(lead: {
  htscNumber?: string
  gstNumber?: string
  createdAt?: string | Date
}): {
  isDeactive: boolean
  gstStatus: 'verified' | 'pending'
  gstVerifyDueDate: string | undefined
  deactiveReason: string | undefined
  missingFields: string[]
  daysLeft: number | undefined
} {
  const missingFields = [
    !lead.htscNumber?.trim() ? 'HTSC Number' : null,
    !lead.gstNumber?.trim() ? 'GST Number' : null,
  ].filter((f): f is string => !!f)

  if (!missingFields.length) {
    return {
      isDeactive: false,
      gstStatus: 'verified',
      gstVerifyDueDate: undefined,
      deactiveReason: undefined,
      missingFields,
      daysLeft: undefined,
    }
  }

  const due = new Date(lead.createdAt ?? Date.now())
  due.setDate(due.getDate() + 2)
  const dueDate = due.toISOString().slice(0, 10)
  const msLeft = due.getTime() - Date.now()
  const overdue = msLeft < 0

  return {
    isDeactive: overdue,
    gstStatus: 'pending',
    gstVerifyDueDate: dueDate,
    deactiveReason: overdue
      ? `Missing ${missingFields.join(' & ')} — 2-day update window from lead creation has passed`
      : undefined,
    missingFields,
    daysLeft: Math.max(0, Math.ceil(msLeft / 86_400_000)),
  }
}
